import { useEffect, useState } from "react";
import { studentService } from "../services/studentService";

// Rank ladder — mirrors the backend's SCHOOL_ROLE_RANK
// (invite.service.ts). Higher number = more privilege. Keep in sync if the
// backend ladder changes.
const SCHOOL_ROLE_RANK = {
  OWNER: 5,
  BILLING: 4,
  ADMIN: 3,
  COUNSELOR: 2,
  CLASSROOM_VIEWER: 1,
};

/** True when `role` ranks at or above `minimum`. Unknown roles rank 0. */
export function rankAtLeast(role, minimum) {
  return (SCHOOL_ROLE_RANK[role] ?? 0) >= (SCHOOL_ROLE_RANK[minimum] ?? 0);
}

/**
 * Fail-OPEN rank check: true unless we positively know `role` ranks below
 * `minimum`. Unknown roles — `undefined` (still loading) or `null` (couldn't
 * resolve, e.g. an older API that omits schoolRole, or a failed fetch) — pass.
 * The backend still enforces the real gate (403), so the client check is only
 * a nicety; we never lock a legitimate admin out over missing client data.
 */
function canAtLeast(role, minimum) {
  if (role === undefined || role === null) return true;
  return rankAtLeast(role, minimum);
}

/** Whether to surface staff-management UI. Backend gate: ADMIN+. */
export function canManageStaff(role) {
  return canAtLeast(role, "ADMIN");
}

/**
 * Whether to surface student-management actions (deactivate / depart).
 * Backend gate: ADMIN+. Note viewing the roster, inviting, and exporting are
 * open to all roles, so those stay ungated.
 */
export function canManageStudents(role) {
  return canAtLeast(role, "ADMIN");
}

/**
 * Whether to surface school-settings mutations (name, logo). Backend gate:
 * ADMIN+ ("Only admin-tier school members can change school settings").
 * Viewing settings stays open to all roles.
 */
export function canEditSchoolSettings(role) {
  return canAtLeast(role, "ADMIN");
}

/**
 * Whether to surface "Reference letter". Backend gate: COUNSELOR+ — a
 * classroom viewer can read the roster but shouldn't speak for the school.
 */
export function canWriteReferenceLetters(role) {
  return canAtLeast(role, "COUNSELOR");
}

// Session-scoped cache. getDashboardStats is a heavy query, and the role is
// needed on every page (Sidebar). Memoizing the in-flight promise means the
// stats endpoint is hit at most once for role resolution no matter how many
// components mount the hook or how the user navigates.
let cachedRolePromise = null;

export function fetchSchoolRole() {
  if (!cachedRolePromise) {
    cachedRolePromise = studentService
      .getDashboardStats()
      .then((stats) => stats?.schoolRole ?? null)
      .catch((error) => {
        // Don't poison the cache on a transient failure — clear it so a
        // later mount can retry.
        cachedRolePromise = null;
        console.error("Failed to resolve school role:", error);
        return null;
      });
  }
  return cachedRolePromise;
}

/**
 * Returns the current user's school rank (e.g. "ADMIN"), or `undefined` while
 * it's still loading, or `null` if it couldn't be resolved. Components should
 * treat `undefined` as "don't decide yet" to avoid flashing rank-restricted
 * UI before the role is known.
 */
export function useSchoolRole() {
  const [role, setRole] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    fetchSchoolRole().then((resolved) => {
      if (!cancelled) setRole(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return role;
}
